import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Observable, Subject, takeUntil } from 'rxjs';
import { IGroupFilter } from '../../models/companies-groups.model';
import { refillGroupFilter } from 'src/app/shared/utils/helpers';
import { AppState } from 'src/app/core/app.state';
import { Store } from '@ngrx/store';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { ILocation } from 'src/app/features/customers/models/customers.models';
import { FetchLocations } from 'src/app/features/customers/store/customers.actions';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';

@Component({
  selector: 'app-filter-group',
  templateUrl: './filter-group.component.html',
  styleUrls: ['./filter-group.component.scss']
})
export class FilterGroupComponent implements OnInit, OnDestroy {
  readonly separatorKeysCodes = [ENTER, COMMA] as const;

  @ViewChild('locationsTrigger', { read: MatAutocompleteTrigger })
  locationsTrigger?: MatAutocompleteTrigger;

  currentFilter: Observable<IGroupFilter> = this.store.select(
    (state) => state.companiesGroups.groupFilterState.advanced
  );

  locationsList: ILocation[] = [];
  filterlocations: ILocation[] = [];
  locationFilterKey: string = '';
  isLocationsLoadingMore = false;

  private locationsPage = 1;
  private locationsPages = 1;
  private appendLocations = false;
  private destroy$ = new Subject<void>();

  filter: IGroupFilter = refillGroupFilter();

  constructor(
    public dialogRef: MatDialogRef<FilterGroupComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private store: Store<AppState>
  ) { }

  ngOnInit(): void {
    this.currentFilter.pipe(takeUntil(this.destroy$)).subscribe((filter: IGroupFilter) => {
      this.filter = refillGroupFilter(filter);
    });

    this.store.select((state) => ({
      locations: state.customers.locations,
      metadata: state.customers.metadata
    })).pipe(takeUntil(this.destroy$)).subscribe(({ locations, metadata }) => {
      if (this.appendLocations) {
        this.locationsList = [...this.locationsList, ...locations];
      } else {
        this.locationsList = [...locations];
      }
      if (metadata) {
        this.locationsPages = metadata.pages;
      }
      this.isLocationsLoadingMore = false;
      this.updateFilteredLocations();
    });

    this.fetchLocationsPage(1, false);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  close(): void {
    this.dialogRef.close();
  }

  resetFilters(): void {
    this.filter = refillGroupFilter();
    this.locationFilterKey = '';
    this.fetchLocationsPage(1, false);
  }

  apply(): void {
    this.dialogRef.close(this.filter);
  }

  filterLocations(): void {
    this.locationsPage = 1;
    this.locationsList = [];
    this.fetchLocationsPage(1, false);
  }

  selectLocation(location: ILocation): void {
    if (this.filter?.groupInfoFilter?.locations)
      this.filter.groupInfoFilter.locations.push(location);
    this.locationFilterKey = '';
    this.filterLocations();
  }

  removeLocation(index: number): void {
    this.filter.groupInfoFilter.locations.splice(index, 1);
    this.updateFilteredLocations();
  }

  loadMoreLocations(): void {
    if (this.isLocationsLoadingMore || this.locationsPage >= this.locationsPages) return;
    this.locationsPage++;
    this.fetchLocationsPage(this.locationsPage, true);
  }

  onLocationsPanelOpened(): void {
    setTimeout(() => {
      const panel = this.locationsTrigger?.autocomplete?.panel?.nativeElement;
      if (panel) {
        panel.addEventListener('scroll', this.onLocationsPanelScroll);
      }
    });
  }

  onLocationsPanelClosed(): void {
    const panel = this.locationsTrigger?.autocomplete?.panel?.nativeElement;
    if (panel) {
      panel.removeEventListener('scroll', this.onLocationsPanelScroll);
    }
  }

  onLocationsPanelScroll = (): void => {
    const panel = this.locationsTrigger?.autocomplete?.panel?.nativeElement;
    if (!panel) return;
    const atBottom = panel.scrollHeight - panel.scrollTop - panel.clientHeight < 50;
    if (atBottom && !this.isLocationsLoadingMore) {
      this.loadMoreLocations();
    }
  };

  private fetchLocationsPage(page: number, append: boolean): void {
    this.appendLocations = append;
    if (append) this.isLocationsLoadingMore = true;
    this.store.dispatch(FetchLocations({
      page: { number: page, size: 20 },
      key: this.locationFilterKey || undefined,
      skipGlobalLoader: true
    }));
  }

  private updateFilteredLocations(): void {
    const selectedIds = new Set(this.filter?.groupInfoFilter?.locations?.map((l) => l.id) ?? []);
    this.filterlocations = this.locationsList.filter((l) => !selectedIds.has(l.id));
  }
}
